import { Link } from "react-router-dom";
import {
  Phone, Mail, CalendarCheck, Home as HomeIcon, Droplet, Activity, Gauge, FlaskConical,
  Filter, HeartPulse, Sun, Waves, TestTubes, ShieldCheck, Clock, MapPin, Star,
  CheckCircle2, Search, ArrowRight,
} from "lucide-react";
import { SITE, IMAGES, SERVICES, PACKAGES, FAQS, telLink, mailLink, waLink } from "../data/siteConfig";
import { useSEO } from "../components/SEO";
import FadeIn from "../components/FadeIn";
import SectionTitle from "../components/SectionTitle";
import { CallCTA, WhatsAppCTA, BookCTA, btnWhite, btnOutlineWhite, btnWhatsApp, btnPrimary, btnOutline } from "../components/CTAs";
import TestFinder from "../components/TestFinder";
import FaqAccordion from "../components/FaqAccordion";
import FinalCTA from "../components/FinalCTA";

const ICONS = { Droplet, Activity, Gauge, FlaskConical, Filter, HeartPulse, Sun, Waves, TestTubes };

const quickBar = [
  { icon: Phone, title: "Call Us", value: SITE.phone, href: telLink, testId: "quickbar-call" },
  { icon: Mail, title: "Email Us", value: SITE.email, href: mailLink, testId: "quickbar-email" },
  { icon: CalendarCheck, title: "Book a Test", value: "Easy enquiry", href: "/book-a-test", testId: "quickbar-book" },
  { icon: HomeIcon, title: "Home Collection", value: "Enquire now", href: "/home-sample-collection", testId: "quickbar-home-collection" },
];

const lookingFor = [
  { icon: Droplet, title: "Blood Tests", desc: "Explore available blood investigations.", cta: "View Tests", to: "/tests", testId: "looking-blood-tests" },
  { icon: HomeIcon, title: "Home Sample Collection", desc: "Request convenient sample collection.", cta: "Request Collection", to: "/home-sample-collection", testId: "looking-home-collection" },
  { icon: HeartPulse, title: "Health Checkups", desc: "Explore available health checkup options.", cta: "Explore Packages", to: "/health-packages", testId: "looking-health-checkups" },
  { icon: CalendarCheck, title: "Book a Test", desc: "Send your test enquiry.", cta: "Book Now", to: "/book-a-test", testId: "looking-book-test" },
];

const steps = [
  { n: "01", title: "Choose Your Test", desc: "Select the diagnostic test or package you need." },
  { n: "02", title: "Book an Appointment", desc: "Contact the collection center or submit the online booking form." },
  { n: "03", title: "Sample Collection", desc: "Visit the collection center or request home sample collection where available." },
  { n: "04", title: "Receive Your Report", desc: "Receive your report through the applicable reporting channel." },
];

const benefits = [
  "Easy appointment enquiries",
  "Professional sample collection",
  "Convenient service",
  "Home collection enquiry option",
  "Wide range of diagnostic services",
  "Friendly patient support",
];

const HomePage = () => {
  useSEO(
    "Dr Lal PathLabs Collection Center Bhopal | Diagnostic Tests & Home Sample Collection",
    "Dr Lal PathLabs Collection Center in Bhopal. Enquire about diagnostic tests, health checkups and convenient sample collection services. Call 8959830932."
  );

  return (
    <div>
      <section className="relative overflow-hidden bg-[#0B3056]">
        <img src={IMAGES.hero} alt="Medical professional working in a modern diagnostic laboratory" className="absolute inset-0 h-full w-full object-cover" fetchpriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B3056]/95 via-[#0B3056]/80 to-[#0B3056]/30" aria-hidden="true" />
        <div className="relative mx-auto flex min-h-[560px] max-w-7xl items-center px-4 py-20 sm:px-6 lg:px-8">
          <FadeIn className="max-w-2xl">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">Trusted in Bhopal</p>
            <h1 className="font-heading text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Trusted Diagnostic Testing &amp; Sample Collection in Bhopal
            </h1>
            <p className="mt-5 max-w-xl text-base text-blue-100 md:text-lg">
              Convenient diagnostic services, sample collection and patient-friendly support for your testing needs.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <BookCTA label="Book a Test" className={btnWhite} testId="hero-book-test-button" />
              <CallCTA label={`Call ${SITE.phone}`} className={btnOutlineWhite} testId="hero-call-button" />
              <WhatsAppCTA className={btnWhatsApp} testId="hero-whatsapp-button" />
            </div>
            <p className="mt-6 text-sm text-blue-200">Diagnostic testing • Sample collection • Patient support</p>
          </FadeIn>
        </div>
      </section>

      <section className="relative z-10 mx-auto -mt-12 max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="grid gap-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-lg sm:grid-cols-2 lg:grid-cols-4">
            {quickBar.map((q) => {
              const Icon = q.icon;
              const inner = (
                <>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F0F7FA] text-[#1E88E5]">
                    <Icon size={20} aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{q.title}</span>
                    <span className="block truncate text-sm font-semibold text-[#0B3056]">{q.value}</span>
                  </span>
                </>
              );
              return q.href.startsWith("/") ? (
                <Link key={q.title} to={q.href} data-testid={q.testId} className="flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-[#F0F7FA]">{inner}</Link>
              ) : (
                <a key={q.title} href={q.href} data-testid={q.testId} className="flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-[#F0F7FA]">{inner}</a>
              );
            })}
          </div>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionTitle overline="Quick Actions" title="What Are You Looking For?" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {lookingFor.map((c, i) => {
            const Icon = c.icon;
            return (
              <FadeIn key={c.title} delay={i * 0.06} className="h-full">
                <Link
                  to={c.to}
                  data-testid={c.testId}
                  className="group flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-7 shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F0F7FA] text-[#1E88E5] transition-transform duration-200 group-hover:scale-110">
                    <Icon size={26} aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-[#0B3056]">{c.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-slate-600">{c.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E88E5]">
                    {c.cta} <ArrowRight size={16} aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </FadeIn>
            );
          })}
        </div>
      </section>

      <section className="bg-[#F0F7FA] py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <FadeIn>
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img src={IMAGES.about} alt="Laboratory professional examining a sample through a microscope" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#1E88E5]">About Us</p>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0B3056] sm:text-3xl">About Our Collection Center</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              {SITE.fullName} provides convenient diagnostic sample collection services for patients in Bhopal, Madhya
              Pradesh. Our focus is on making diagnostic testing simple — easy appointment enquiries, professional and
              hygienic sample collection, and friendly patient support at every step.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Whether you visit our collection center or enquire about home sample collection, our team helps you with
              test information, availability and booking so your testing experience stays smooth and stress-free.
            </p>
            <div className="mt-7">
              <Link to="/about" data-testid="about-learn-more-button" className={btnOutline}>Learn More</Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionTitle
          overline="Our Services"
          title="Our Diagnostic Services"
          subtitle="Explore diagnostic testing and sample collection services available through our collection center."
        />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || TestTubes;
            return (
              <FadeIn key={s.title} delay={(i % 3) * 0.06} className="h-full">
                <article className="flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-7 shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F0F7FA] text-[#1E88E5]">
                    <Icon size={22} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-[#0B3056]">{s.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-slate-600">{s.desc}</p>
                  <a
                    href={waLink(`Hello, I would like to enquire about ${s.title} at Dr Lal PathLabs Collection Center, Bhopal.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-enquire-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="mt-5 inline-flex min-h-[40px] items-center justify-center rounded-full border border-[#1E88E5] px-4 py-2 text-xs font-semibold text-[#1E88E5] transition-colors hover:bg-[#F0F7FA]"
                  >
                    Enquire Now
                  </a>
                </article>
              </FadeIn>
            );
          })}
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#0B3056]">
        <img src={IMAGES.homeCollection} alt="Healthcare professional visiting a patient at home for sample collection" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <FadeIn className="max-w-xl">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Prefer to Stay at Home?</h2>
            <p className="mt-4 text-base text-blue-100 md:text-lg">
              Ask about convenient home sample collection in Bhopal. Our team will confirm availability and the
              applicable collection details for your area.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/book-a-test?type=home" data-testid="home-collection-banner-button" className={btnWhite}>Request Home Collection</Link>
              <WhatsAppCTA message="Hello, I would like to enquire about home sample collection at Dr Lal PathLabs Collection Center, Bhopal." className={btnWhatsApp} testId="home-collection-banner-whatsapp" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-[#F0F7FA] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="Simple Process" title="How It Works" align="center" />
          <div className="grid gap-8 md:grid-cols-4">
            {steps.map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.08} className="relative">
                <div className="flex flex-col items-center text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#1E88E5] font-heading text-xl font-extrabold text-white shadow-md">
                    {s.n}
                  </span>
                  {i < steps.length - 1 && (
                    <span className="absolute left-[calc(50%+40px)] top-8 hidden h-0.5 w-[calc(100%-80px)] bg-[#1E88E5]/25 md:block" aria-hidden="true" />
                  )}
                  <h3 className="mt-5 font-heading text-lg font-bold text-[#0B3056]">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <FadeIn>
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img src={IMAGES.blood} alt="Blood test sample vials in a diagnostic laboratory" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#1E88E5]">Why Patients Choose Us</p>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0B3056] sm:text-3xl">Simple. Convenient. Patient-Focused.</h2>
            <ul className="mt-6 space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-base text-slate-700">
                  <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-[#1E88E5]" aria-hidden="true" />
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <BookCTA label="Book a Test" className={btnPrimary} testId="why-choose-book-button" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-[#F0F7FA] py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="Test Finder" title="Find the Test You Need" subtitle="Search common diagnostic tests and send your enquiry in a few taps." align="center" />
          <TestFinder preview />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionTitle
          overline="Preventive Health"
          title="Health Checkup Packages"
          subtitle="Ask about available preventive health checkups and diagnostic packages."
        />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PACKAGES.slice(0, 4).map((p, i) => (
            <FadeIn key={p.name} delay={i * 0.06} className="h-full">
              <article className="flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-7 shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`package-card-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F0F7FA] text-[#1E88E5]">
                  <ShieldCheck size={22} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-[#0B3056]">{p.name}</h3>
                <p className="mt-2 flex-1 text-sm text-slate-600">{p.desc}</p>
                <p className="mt-4 text-xs font-medium uppercase tracking-wide text-slate-500">Availability &amp; details — enquire now</p>
                <a
                  href={waLink(`Hello, I would like to enquire about the ${p.name} package at Dr Lal PathLabs Collection Center, Bhopal.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`package-enquire-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="mt-3 inline-flex min-h-[40px] items-center justify-center rounded-full border border-[#1E88E5] px-4 py-2 text-xs font-semibold text-[#1E88E5] transition-colors hover:bg-[#F0F7FA]"
                >
                  Enquire About Package
                </a>
              </article>
            </FadeIn>
          ))}
        </div>
        <FadeIn className="mt-8 text-center">
          <Link to="/health-packages" data-testid="view-all-packages-link" className={btnOutline}>View All Packages</Link>
        </FadeIn>
      </section>

      <section className="bg-[#F0F7FA] py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <FadeIn>
            <div className="flex justify-center gap-1 text-[#1E88E5]" aria-hidden="true">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={22} className="fill-[#F0F7FA]" />
              ))}
            </div>
            <h2 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-[#0B3056] sm:text-3xl">What Our Patients Say</h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
              Patient reviews will appear here once connected to the center&apos;s verified review profile.
            </p>
            <a
              href={waLink("Hello, I would like to share feedback about my experience at Dr Lal PathLabs Collection Center, Bhopal.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leave-review-button"
              className="mt-6 inline-flex min-h-[44px] items-center rounded-full border-2 border-[#1E88E5] px-6 py-3 text-sm font-semibold text-[#1E88E5] transition-colors hover:bg-white"
            >
              Leave a Review
            </a>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionTitle overline="Common Questions" title="Frequently Asked Questions" align="center" />
        <FaqAccordion items={FAQS.slice(0, 4)} prefix="home-faq" />
        <FadeIn className="mt-8 text-center">
          <Link to="/faq" data-testid="view-all-faq-link" className={btnOutline}>View All FAQs</Link>
        </FadeIn>
      </section>

      <section className="bg-[#F0F7FA] py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0B3056] sm:text-3xl">
              Diagnostic Sample Collection Services in Bhopal
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              {SITE.fullName} serves patients across Bhopal, Madhya Pradesh with convenient diagnostic testing and
              sample collection services. From routine blood tests and health checkups to specialized laboratory
              investigations, our collection center helps you access pathology testing with an easy enquiry and
              appointment process.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              You can visit our collection center in Bhopal or contact us to enquire about home sample collection in
              your area. For test availability, health checkup packages and current prices, call us on{" "}
              <a href={telLink} className="font-semibold text-[#1E88E5]" data-testid="local-seo-phone-link">{SITE.phone}</a>{" "}
              or send us a message on WhatsApp.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6">
        <FadeIn>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0B3056] sm:text-3xl">Need Help Choosing a Test?</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-600">
            Our collection center team can help with test and appointment enquiries.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <CallCTA label={`Call ${SITE.phone}`} testId="help-call-button" />
            <WhatsAppCTA testId="help-whatsapp-button" />
            <BookCTA className={btnPrimary} testId="help-book-button" />
          </div>
        </FadeIn>
      </section>

      <FinalCTA />
    </div>
  );
};

export default HomePage;
